/**
 * Welcome-video uploads, keyed by what they belong to (an intake, or a
 * template), outside React so one keeps going while the attorney moves
 * between pages. The video card, the page bar and Send all read the same
 * entry: Send waits for it, Cancel aborts it.
 */
import { useSyncExternalStore } from "react";
import { toast } from "sonner";

export type VideoUpload = {
  /** Share sent, 0 to 1. */
  progress: number;
  cancel: () => void;
};

const uploads = new Map<string, VideoUpload>();
const owners = new Map<string, AbortController>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

type StartArgs<T> = {
  key: string;
  /** Sends the file; must honor `signal` so Cancel can stop it. */
  upload: (
    onProgress: (fraction: number) => void,
    signal: AbortSignal
  ) => Promise<T>;
  onDone: (result: T) => void;
  doneMessage?: string;
};

export const startVideoUpload = <T>({
  key,
  upload,
  onDone,
  doneMessage = "Welcome video saved"
}: StartArgs<T>): void => {
  uploads.get(key)?.cancel();
  const controller = new AbortController();
  owners.set(key, controller);
  const clear = () => {
    if (owners.get(key) !== controller) return;
    owners.delete(key);
    uploads.delete(key);
    emit();
  };
  const set = (progress: number) => {
    if (controller.signal.aborted) return;
    uploads.set(key, { progress, cancel: () => controller.abort() });
    emit();
  };
  // Cancel drops the entry right away; the request winds down on its own.
  controller.signal.addEventListener("abort", clear);
  set(0);
  upload(set, controller.signal)
    .then((result) => {
      if (controller.signal.aborted) return;
      onDone(result);
      toast.success(doneMessage);
    })
    .catch((e: unknown) => {
      if (controller.signal.aborted) return;
      toast.error(e instanceof Error ? e.message : "Couldn't upload the video");
    })
    .finally(clear);
};

/** The upload in flight for `key`, or null. */
export const useVideoUpload = (key: string | null): VideoUpload | null =>
  useSyncExternalStore(
    subscribe,
    () => (key ? uploads.get(key) ?? null : null),
    () => null
  );

export const intakeVideoKey = (intakeId: string) => `intake:${intakeId}`;
export const templateVideoKey = (templateId: string) =>
  `template:${templateId}`;
